// =====================================================
// MODAL: ConfirmDeleteContactDialog
// Description: Suppression sûre d'une fiche contact (CRM-15).
//   Ouverture -> dry-run RPC delete_contact_safely -> récap d'impact ->
//   confirmation (checkbox force si cas sensible superadmin) -> exécution.
//   Remplace le ConfirmDialog générique + DELETE direct (échec 23503 silencieux).
// =====================================================

import { useEffect, useState } from 'react';
import { useContactActions } from '../../hooks/useContactActions';
import { deleteContactErrorMessage } from '../../lib/contactDeleteErrors';
import type { ContactDeletionImpact } from '../../types/contactDeletion';
import { Button, Checkbox, toast } from '../ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface ConfirmDeleteContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  contactName: string;
  onDeleted: () => void;
}

function formatEuros(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });
}

export function ConfirmDeleteContactDialog({
  open,
  onOpenChange,
  contactId,
  contactName,
  onDeleted,
}: ConfirmDeleteContactDialogProps) {
  const { previewContactDeletion, deleteContactSafely } = useContactActions();

  const [impact, setImpact] = useState<ContactDeletionImpact | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [forceChecked, setForceChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setImpact(null);
    setPreviewError(null);
    setForceChecked(false);
    setLoading(true);
    let cancelled = false;
    previewContactDeletion(contactId).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setImpact(res.impact);
      } else {
        setPreviewError(deleteContactErrorMessage(res.code));
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, contactId, previewContactDeletion]);

  const blocked = impact?.blocked ?? false;
  const requiresForce = impact?.requires_force ?? false;
  const canConfirm =
    !loading &&
    !deleting &&
    !previewError &&
    impact !== null &&
    !blocked &&
    (!requiresForce || forceChecked);

  async function handleConfirm() {
    if (!canConfirm) return;
    setDeleting(true);
    const res = await deleteContactSafely(contactId, forceChecked);
    setDeleting(false);
    if (res.success) {
      toast.success('Contact supprimé');
      onOpenChange(false);
      onDeleted();
    } else {
      toast.error(deleteContactErrorMessage(res.code));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>Supprimer la fiche contact</DialogTitle>
          <DialogDescription>
            {`Suppression de la fiche "${contactName}". Cette action est irréversible.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {loading && <p className="text-muted-foreground">Analyse de l'impact…</p>}

          {previewError && <p className="text-destructive">{previewError}</p>}

          {impact && (
            <>
              {impact.cases_to_delete.length > 0 && (
                <div>
                  <p>{impact.cases_to_delete.length} dossier(s) de paiement supprimé(s) :</p>
                  <ul className="mt-1 list-inside list-disc text-muted-foreground">
                    {impact.cases_to_delete.map((c) => (
                      <li key={c.payment_case_id}>
                        {c.order_id ?? c.payment_case_id} — {c.status} —{' '}
                        {formatEuros(c.amount_cents)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {impact.cases_to_detach.length > 0 && (
                <p>
                  {impact.cases_to_detach.length} dossier(s) détaché(s) et{' '}
                  <span className="font-medium">conservé(s)</span> sur leur équipage.
                </p>
              )}

              {impact.active_crew_memberships.length > 0 && (
                <p className="text-amber-600">
                  Cette fiche est membre de l'équipage{' '}
                  {impact.active_crew_memberships.map((m) => m.crew_number ?? m.crew_id).join(', ')}{' '}
                  (non archivé) : elle en sera retirée.
                </p>
              )}

              {impact.has_destructible_payment && (
                <p className="font-medium text-destructive">
                  Un paiement encaissé sera définitivement supprimé.
                </p>
              )}

              {impact.cases_to_delete.length === 0 &&
                impact.cases_to_detach.length === 0 &&
                impact.active_crew_memberships.length === 0 && (
                  <p className="text-muted-foreground">
                    Aucun dossier de paiement lié : seule la fiche sera supprimée.
                  </p>
                )}

              {blocked && (
                <p className="rounded-md bg-destructive/10 p-2 text-destructive">
                  Action réservée au superadmin : cette fiche est liée à un paiement encaissé ou à
                  un équipage actif.
                </p>
              )}

              {requiresForce && !blocked && (
                <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-2">
                  <Checkbox
                    checked={forceChecked}
                    onChange={setForceChecked}
                    label="Confirmer la suppression forcée"
                    className="mt-0.5"
                  />
                  <span>
                    Je confirme la suppression malgré l'alerte (encaissement détruit et/ou retrait
                    d'un équipage actif).
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={!canConfirm}>
            Supprimer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
